import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Newspaper, ArrowRight, Calendar, Tag } from "lucide-react";
import { Button } from "./ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (_) {
    return "";
  }
}

export default function BlogPreviewHome() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/api/blog?limit=3&page=1`)
      .then((r) => {
        if (!mounted) return;
        const list = Array.isArray(r.data) ? r.data : r.data?.items || [];
        setItems(list.slice(0, 3));
      })
      .catch(() => setItems([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  if (!loading && items.length === 0) return null;

  return (
    <section
      id="blog-preview"
      data-testid="blog-preview-home"
      className="relative py-16 md:py-20 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-2">
              <Newspaper className="h-4 w-4 text-[#f59e0b]" />
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">
                Blog & News
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a1f55] leading-tight">
              Insight, guide e novità dalla rete ACDT.
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Approfondimenti su normativa, tecnologia, sostenibilità e casi reali del
              Consulente Digitale.
            </p>
          </div>
          <Link to="/blog">
            <Button
              data-testid="blog-preview-view-all"
              variant="outline"
              className="border-[#0a1f55] text-[#0a1f55] hover:bg-[#0a1f55] hover:text-white h-11"
            >
              Vedi tutti gli articoli
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6" data-testid="blog-preview-loading">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 bg-slate-50 h-80 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {items.map((a) => (
              <Link
                key={a.id}
                to={`/blog/${a.slug}`}
                data-testid={`blog-preview-card-${a.id}`}
                className="group flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-[#f59e0b]/50 hover:shadow-xl transition-all"
              >
                <div className="relative h-44 bg-gradient-to-br from-[#0a1f55] to-[#102a6b] overflow-hidden">
                  {a.cover_url ? (
                    <img
                      src={a.cover_url.startsWith("http") ? a.cover_url : `${API}${a.cover_url}`}
                      alt={a.titolo}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Newspaper className="h-14 w-14 text-white/20" />
                    </div>
                  )}
                  {a.categoria && (
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[10px] font-extrabold uppercase tracking-wider">
                      {a.categoria}
                    </div>
                  )}
                </div>

                <div className="flex-1 flex flex-col p-5">
                  <div className="flex items-center gap-3 text-[11px] text-slate-500 uppercase tracking-wider mb-2">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(a.pubblicato_at)}
                    </span>
                    {a.autore && (
                      <>
                        <span>·</span>
                        <span className="truncate max-w-[130px]">{a.autore}</span>
                      </>
                    )}
                  </div>

                  <h3 className="text-lg font-extrabold text-[#0a1f55] leading-snug line-clamp-2 group-hover:text-[#102a6b]">
                    {a.titolo}
                  </h3>

                  {a.excerpt && (
                    <p className="mt-2 text-[14px] text-slate-600 leading-relaxed line-clamp-3 flex-1">
                      {a.excerpt}
                    </p>
                  )}

                  {Array.isArray(a.tags) && a.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {a.tags.slice(0, 2).map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10.5px] font-semibold"
                        >
                          <Tag className="h-2.5 w-2.5" />
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 inline-flex items-center gap-1 text-[13px] font-bold text-[#f59e0b] group-hover:gap-2 transition-all">
                    Leggi l&apos;articolo
                    <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
